import type { CSSProperties } from 'react';

import type { NotificationRecord } from './model';

const NOTIFICATION_TONE_STYLES: Record<
  'event' | 'user' | 'system',
  CSSProperties
> = {
  event: {
    backgroundColor: '#dbeafe',
    color: '#2563eb',
  },
  user: {
    backgroundColor: '#dcfce7',
    color: '#16a34a',
  },
  system: {
    backgroundColor: '#f3e8ff',
    color: '#9333ea',
  },
};

export const getNotificationRoute = (
  notification: NotificationRecord
): string | null => {
  if (!notification.referenceId) {
    return null;
  }

  if (notification.referenceType === 'event') {
    return `/events/view/${notification.referenceId}`;
  }

  if (notification.referenceType === 'deleted_user') {
    return `/users/deleted/${notification.referenceId}`;
  }

  if (notification.referenceType === 'user') {
    return `/users/view/${notification.referenceId}`;
  }

  return null;
};

export const getNotificationBadgeLabel = (
  notification: NotificationRecord
): string => {
  if (notification.referenceType === 'event') {
    return 'event';
  }

  if (
    notification.referenceType === 'user' ||
    notification.referenceType === 'deleted_user'
  ) {
    return 'user';
  }

  return 'system';
};

export const getNotificationBadgeClassName = (
  notification: NotificationRecord
): string => {
  const label = getNotificationBadgeLabel(notification);

  if (label === 'event') {
    return 'border border-transparent';
  }

  if (label === 'user') {
    return 'border border-transparent';
  }

  return 'border border-transparent';
};

export const getNotificationBadgeStyle = (
  notification: NotificationRecord
): CSSProperties => {
  const label = getNotificationBadgeLabel(notification);
  return NOTIFICATION_TONE_STYLES[label];
};

export const formatRelativeNotificationTime = (value?: string | null): string => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.max(0, Math.floor(diffMs / 60000));

  if (diffMinutes < 1) {
    return 'Just now';
  }

  if (diffMinutes < 60) {
    return `${diffMinutes} min ago`;
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  }

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
};
